import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Button,
  Chip,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import DetailsBox from '../../../../../components/Cards/DetailsBox'
import StatCard from '../../../../../components/Cards/StatCard'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded'
import MedicalServicesRoundedIcon from '@mui/icons-material/MedicalServicesRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import API from '../../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../../App'
import { EventMetaContext } from '../../SingleEvent'
import AddEventExpense from './AddEventExpense'
import CustomDataGrid from '../../../../../components/DataGrid/CustomDataGrid'

const statusColors = { Pending: 'default', Approved: 'success', Rejected: 'error' }

const EventCosts = () => {
  const { eventMeta } = useContext(EventMetaContext)
  const { setToast } = useContext(GlobalContext)
  const { orgConfig } = useContext(TenantSettingsContext)
  const currency = orgConfig?.orgSettings?.currency ?? '£'

  const [breakdown, setBreakdown] = useState(null)
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [openAddExpense, setOpenAddExpense] = useState(false)

  const loadData = () => {
    if (!eventMeta?._id) return
    setLoading(true)
    Promise.all([
      API.get(`/events/${eventMeta._id}/cost-breakdown`),
      API.get(`/events/${eventMeta._id}/expenses`),
    ])
      .then(([breakdownRes, expensesRes]) => {
        if (breakdownRes.status === 200) setBreakdown(breakdownRes.data)
        if (expensesRes.status === 200) setExpenses(expensesRes.data || [])
      })
      .finally(() => setLoading(false))
  }

  useEffect(loadData, [eventMeta?._id])

  const updateExpenseStatus = async (expenseID, status) => {
    const res = await API.put(`/expenses/${expenseID}/status`, { status })
    if (res.status === 200) {
      loadData()
      setToast({ message: `Expense ${status.toLowerCase()}` })
    } else {
      setToast({
        message: res?.data?.error || 'Unable to update this expense.',
        severity: 'error',
      })
    }
  }

  const deleteExpense = async (expenseID) => {
    const res = await API.delete(`/expenses/${expenseID}`)
    if (res.status === 200) {
      loadData()
      setToast({ message: 'Expense removed' })
    } else {
      setToast({
        message: res?.data?.error || 'Unable to remove this expense.',
        severity: 'error',
      })
    }
  }

  const money = (value) => `${currency}${Number(value || 0).toFixed(2)}`

  return (
    <Stack gap={2}>
      <Alert severity="info" icon={<InfoOutlinedIcon />}>
        These figures are best-effort estimates, not exact accounting — vehicle
        cost is derived from each vehicle's real service history where
        available (falling back to a tenant default), and consumable cost only
        covers items matched to the Stock Catalog.
      </Alert>
      {loading || !breakdown ? (
        <Typography variant="body2" color="text.secondary">
          Loading cost breakdown...
        </Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                icon={GroupsRoundedIcon}
                label="Staff Cost"
                value={money(breakdown.staffCost)}
                caption={`${breakdown.shiftCount} filled shift${breakdown.shiftCount === 1 ? '' : 's'}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                icon={DirectionsCarRoundedIcon}
                label="Vehicle Cost"
                value={money(breakdown.vehicleCost)}
                caption={`${breakdown.vehicleLines.length} deployment${breakdown.vehicleLines.length === 1 ? '' : 's'} (est.)`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                icon={MedicalServicesRoundedIcon}
                label="Consumables"
                value={money(breakdown.consumablesCost)}
                caption={
                  breakdown.consumablesUncostedCount > 0
                    ? `${breakdown.consumablesUncostedCount} item(s) uncosted`
                    : 'All items costed'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                icon={ReceiptLongRoundedIcon}
                label="Other Expenses"
                value={money(breakdown.approvedExpenses)}
                caption={
                  breakdown.pendingExpenses > 0
                    ? `${money(breakdown.pendingExpenses)} pending approval`
                    : 'None pending'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                icon={PaidRoundedIcon}
                label="Total Cost"
                value={money(breakdown.totalCost)}
                color="anRed"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                icon={TrendingUpRoundedIcon}
                label="Margin"
                value={breakdown.margin != null ? `${breakdown.margin.toFixed(0)}%` : '—'}
                caption={
                  breakdown.invoicedTotal > 0
                    ? `Invoiced ${money(breakdown.invoicedTotal)}`
                    : 'No invoice yet'
                }
                color={
                  breakdown.margin == null
                    ? 'anBlue'
                    : breakdown.margin >= 20
                      ? 'anGreen'
                      : breakdown.margin >= 0
                        ? 'anOrange'
                        : 'anRed'
                }
              />
            </Grid>
          </Grid>

          {breakdown.vehicleLines.length > 0 && (
            <DetailsBox title="Vehicle Cost Detail">
              <Stack gap={1}>
                {breakdown.vehicleLines.map((line, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">
                      {line.vehicle} — {line.days} day{line.days === 1 ? '' : 's'} ×{' '}
                      {money(line.dailyCost)}/day
                      {line.estimated && (
                        <Tooltip title="No service history yet — using tenant default daily cost">
                          <Chip
                            size="small"
                            label="default rate"
                            sx={{ ml: 1 }}
                          />
                        </Tooltip>
                      )}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {money(line.cost)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </DetailsBox>
          )}

          <DetailsBox
            title="Event Expenses"
            buttonRight={
              <Button
                size="small"
                variant="contained"
                color="anPurple"
                onClick={() => setOpenAddExpense(true)}
              >
                Add Expense
              </Button>
            }
          >
            {expenses.length ? (
              <CustomDataGrid
                rows={expenses}
                getRowId={(row) => row._id}
                disableRowSelectionOnClick
                slots={{ footer: () => null, toolbar: () => null }}
                columns={[
                  {
                    field: 'expenseDate',
                    headerName: 'Date',
                    minWidth: 120,
                    renderCell: ({ value }) => moment(value).format('DD/MM/YYYY'),
                  },
                  { field: 'category', headerName: 'Category', minWidth: 130 },
                  {
                    field: 'description',
                    headerName: 'Description',
                    minWidth: 200,
                    flex: 1,
                  },
                  {
                    field: 'amount',
                    headerName: 'Amount',
                    minWidth: 110,
                    renderCell: ({ value }) => money(value),
                  },
                  {
                    field: 'status',
                    headerName: 'Status',
                    minWidth: 130,
                    renderCell: ({ value }) => (
                      <Chip size="small" label={value} color={statusColors[value] || 'default'} />
                    ),
                  },
                  {
                    field: 'actions',
                    headerName: '',
                    minWidth: 200,
                    sortable: false,
                    renderCell: ({ row }) => (
                      <Stack direction="row" gap={1}>
                        {row.status === 'Pending' && (
                          <>
                            <Button
                              size="small"
                              color="anGreen"
                              onClick={() => updateExpenseStatus(row._id, 'Approved')}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              color="anRed"
                              onClick={() => updateExpenseStatus(row._id, 'Rejected')}
                            >
                              Reject
                            </Button>
                            <Button size="small" onClick={() => deleteExpense(row._id)}>
                              Delete
                            </Button>
                          </>
                        )}
                      </Stack>
                    ),
                  },
                ]}
              />
            ) : (
              <Alert severity="info">No expenses recorded for this event yet</Alert>
            )}
          </DetailsBox>
        </>
      )}
      <AddEventExpense
        open={openAddExpense}
        onClose={() => setOpenAddExpense(false)}
        eventID={eventMeta?._id}
        onAdded={loadData}
      />
    </Stack>
  )
}

export default EventCosts
